import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import type { PartyType } from "../api/types";
import { useAuth, useSession } from "../auth/AuthProvider";
import { hasStaffRole, STAFF_HOME } from "../auth/guards";
import { BrandMark, Icon, type IconName } from "../components/Icon";
import { Button } from "../components/ui";
import { SpendFreezeBanner } from "../pages/advertiser/Wallet";
import { NotificationBell } from "../pages/shared/org/Notifications";
import { ClosedAccountScreen, closedAccountAllows } from "./ClosedAccount";
import { EnvironmentIndicator } from "./EnvironmentIndicator";
import { SystemStatusBanners } from "./SystemStatusBanners";
import { ThemeToggle } from "./ThemeToggle";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /** Screen exists but its data needs an endpoint that isn't built yet. */
  pending?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const ADVERTISER_NAV: NavGroup[] = [
  {
    label: "Advertise",
    items: [
      { to: "/advertiser", label: "Overview", icon: "home", end: true },
      { to: "/advertiser/campaigns", label: "Campaigns", icon: "megaphone" },
    ],
  },
  {
    label: "Money",
    items: [
      { to: "/advertiser/wallet", label: "Wallet", icon: "wallet" },
      { to: "/advertiser/refunds", label: "Refunds", icon: "undo" },
      { to: "/advertiser/statements", label: "Statements", icon: "receipt" },
      { to: "/advertiser/disputes", label: "Billing disputes", icon: "scale" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/advertiser/security", label: "Sign-in & security", icon: "shield" },
      { to: "/advertiser/organization", label: "Organization", icon: "users" },
      { to: "/advertiser/close-account", label: "Close account", icon: "door" },
    ],
  },
];

const PUBLISHER_NAV: NavGroup[] = [
  {
    label: "Publish",
    items: [
      { to: "/publisher", label: "Overview", icon: "home", end: true },
      { to: "/publisher/sites", label: "Sites", icon: "globe" },
    ],
  },
  {
    label: "Money",
    items: [
      { to: "/publisher/earnings", label: "Earnings", icon: "chart" },
      { to: "/publisher/payouts", label: "Payouts", icon: "banknote" },
      { to: "/publisher/payout-setup", label: "Payout setup", icon: "lock" },
      { to: "/publisher/statements", label: "Statements", icon: "receipt" },
      { to: "/publisher/disputes", label: "Disputes", icon: "scale" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/publisher/security", label: "Sign-in & security", icon: "shield" },
      { to: "/publisher/organization", label: "Organization", icon: "users" },
      { to: "/publisher/close-account", label: "Close account", icon: "door" },
    ],
  },
];

export function navFor(partyType: PartyType): NavGroup[] {
  return partyType === "advertiser" ? ADVERTISER_NAV : PUBLISHER_NAV;
}

/** A closed account (0065) keeps only its statements and the closure record. */
function closedNav(home: string): NavGroup[] {
  return [
    {
      label: "Closed account",
      items: [
        { to: `${home}/statements`, label: "Statements", icon: "receipt" },
        { to: `${home}/close-account`, label: "Closure record", icon: "door" },
      ],
    },
  ];
}

export function AppShell() {
  const session = useSession();
  const { logout, accountClosed } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setNavOpen(false), [location.pathname]);

  // Move focus to the main region on route change so screen readers land on the new page.
  useEffect(() => {
    const main = document.getElementById("main");
    if (main && document.activeElement !== main && !navOpen) main.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const home = session.partyType === "advertiser" ? "/advertiser" : "/publisher";
  const groups = accountClosed ? closedNav(home) : navFor(session.partyType);
  const portalName = session.partyType === "advertiser" ? "Advertiser" : "Publisher";
  // migration 0070 (cmd/ops grant-reviewer) can grant platform_reviewer to
  // an existing advertiser/publisher account; that dual-role session has
  // no other way to discover the staff console (auth/guards.tsx RequireStaff).
  const navGroups = hasStaffRole(session.me)
    ? [...groups, { label: "Staff", items: [{ to: STAFF_HOME, label: "Staff console", icon: "shield" as const }] }]
    : groups;

  return (
    <div className="shell" data-nav-open={navOpen}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className="shell-sidebar" aria-label={`${portalName} navigation`}>
        <NavLink to={session.partyType === "advertiser" ? "/advertiser" : "/publisher"} className="brand">
          <BrandMark />
          <span>
            VADS <span className="brand-portal">{portalName}</span>
          </span>
        </NavLink>
        <nav className="nav">
          {navGroups.map((g) => (
            <div key={g.label}>
              <div className="nav-group-label" id={`nav-${g.label}`}>
                {g.label}
              </div>
              <ul aria-labelledby={`nav-${g.label}`}>
                {g.items.map((item) => (
                  <li key={item.to}>
                    <NavLink to={item.to} end={item.end} className="nav-link">
                      <Icon name={item.icon} />
                      <span>{item.label}</span>
                      {item.pending ? <span className="nav-soon">Soon</span> : null}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="shell-sidebar-footer">
          <span className="muted xs break-all">
            {session.me.legal_name ?? "Account"} · {session.me.email}
          </span>
          <ThemeToggle />
          <Button variant="secondary" size="sm" onClick={() => void logout()}>
            <Icon name="logout" />
            Sign out
          </Button>
        </div>
      </aside>
      <div className="shell-main">
        <header className="shell-topbar">
          <button
            type="button"
            className="btn btn-ghost btn-sm menu-toggle"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            <Icon name={navOpen ? "x" : "menu"} />
            Menu
          </button>
          <span className="small muted">{portalName} portal</span>
          <span className="row">
            {accountClosed ? null : <NotificationBell />}
            <EnvironmentIndicator />
          </span>
        </header>
        <main id="main" className="shell-content" tabIndex={-1}>
          {/* A closed account: only statements and the closure record still answer
              (session_middleware.go:118-125); the status, bell and wallet reads would each
              be refused, so they aren't made. Positions are stable so the page keeps its state. */}
          {accountClosed ? null : <SystemStatusBanners partyType={session.partyType} />}
          {!accountClosed && session.partyType === "advertiser" ? <SpendFreezeBanner /> : null}
          {accountClosed && !closedAccountAllows(location.pathname, home) ? <ClosedAccountScreen home={home} /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
